import { Empty, Pagination, Spin } from 'antd';
import { cloneDeep, get, isEmpty } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AiCheckBox, BasicButton } from '../../../../../assets/common';
import { dropdown_builder } from '../../../../../assets/img';
import RangeCalendar from '../../../../../components/RangeCalendar/RangeCalendar';
import { auditLogApi } from '../../../../../services/auditLog';
import { DATE_TIME_FORMAT } from '../../../../../utils/constants';
import { getNoByIndex, paginationConfig } from '../../../../../utils/helper';
import * as styles from './ai-pipeline-run.module.scss';

const LIMIT_ROWS = 15;
const STATUS_STYLE = {
  Failed: { background: '#FBE2E2', color: '#A91A1F' },
  InProgress: { background: '#FEF2DC', color: '#C4861C', width: '80px' },
  Cancelled: { background: '#F5F5F5', color: '#575757', width: '70px' },
  Succeeded: { background: '#E7F4E8', color: '#096924', width: '80px' },
};
const INIT_DATA = { records: [], total: 0 };
const INT_APPLIED_RANGE = { type: '', range: [null, null] };

const AiPipelineRun = ({ page }) => {
  const [data, setData] = useState(INIT_DATA);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({
    page: Number(page) || 1,
    pipeline: null,
    start_time: null,
    end_time: null,
    status: null,
    limit: LIMIT_ROWS,
  });
  const [filterOptions, setFilterOptions] = useState({
    process_name: null,
    status: null,
    isGetFilterOptions: false,
  });
  const [appliedRange, setAppliedRange] = useState(INT_APPLIED_RANGE);
  const [expandIds, setExpandIds] = useState([]);
  const roleActive = useSelector((state) => state.user.roleActive);

  const pagination = useMemo(() => {
    const onChange = (page) => setFilter((prev) => ({ ...prev, page }));
    return paginationConfig(data.total, filter.page, onChange, LIMIT_ROWS);
  }, [data.total, filter.page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await auditLogApi.getAiPipelineRun({ time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone, ...filter }, roleActive);
      const data = get(res, 'data.result.data') || [];
      const total = get(res, 'data.result.total');
      setData({ records: data, total });
    } catch (error) {
      console.error(error);
      setData({ records: [], total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const [renderCount, setRenderCount] = useState(0);
  useEffect(() => {
    setRenderCount((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!roleActive.roleId || !filterOptions.isGetFilterOptions) return;
    fetchData();
    const interval = setInterval(fetchData, 600000);
    return () => clearInterval(interval);
  }, [filter.page, roleActive.roleId, filter.pipeline, filter.start_time, filter.end_time, filter.status, filterOptions.isGetFilterOptions]);

  const handleClickExpand = (id) => {
    const newExpandIds = cloneDeep(expandIds);
    if (newExpandIds.includes(id)) {
      setExpandIds(newExpandIds.filter((expandId) => expandId !== id));
      return;
    }
    newExpandIds.push(id);
    setExpandIds(newExpandIds);
  };

  const handleApply = (filterName, selected) => {
    setFilter((prev) => ({
      ...prev,
      page: 1,
      [filterName]: selected,
    }));
  };

  useEffect(() => {
    if (renderCount === 0) return;
    const is1RangeApplied = appliedRange.type && (!appliedRange.range[0] || !appliedRange.range[1]);
    const range = { start_time: null, end_time: null };
    if (is1RangeApplied) {
      range[appliedRange.type] = appliedRange.range[0].toISOString();
    } else {
      range.start_time = appliedRange.range[0] ? appliedRange.range[0].toISOString() : null;
      range.end_time = appliedRange.range[1] ? appliedRange.range[1].toISOString() : null;
    }
    setFilter((prev) => ({
      ...prev,
      page: 1,
      ...range,
    }));
  }, [appliedRange]);

  useEffect(() => {
    const fetchFilter = async () => {
      try {
        setLoading(true);
        const res = await auditLogApi.getFilterAiPipelineRun();
        if (res.status === 200) {
          const result = res.data.result.data;
          const filters = {
            pipeline: result.pipelines,
            status: result.statuses,
          };

          setFilter((prev) => ({
            ...prev,
            ...filters,
          }));
          setFilterOptions({ ...filters, isGetFilterOptions: true });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchFilter();
  }, []);

  return (
    <Spin spinning={loading}>
      {!isEmpty(data) && <div className={styles.title}>AI Pipeline Run</div>}
      <div className={styles.wrapper}>
        <table>
          <thead>
            <tr>
              <th>No.</th>
              <th>Activity</th>
              <th>
                <Dropdown
                  options={filterOptions.pipeline}
                  onApply={(selected) => {
                    handleApply('pipeline', selected);
                  }}
                  loading={loading}
                >
                  <span>Model</span>
                </Dropdown>
              </th>
              <th>
                <RangePicker appliedRange={appliedRange} setAppliedRange={setAppliedRange} loading={loading} type="start_time">
                  Start Date & Time
                </RangePicker>
              </th>
              <th>
                <RangePicker appliedRange={appliedRange} setAppliedRange={setAppliedRange} loading={loading} type="end_time">
                  End Date & Time
                </RangePicker>
              </th>
              <th>
                <Dropdown
                  options={filterOptions.status}
                  onApply={(selected) => {
                    handleApply('status', selected);
                  }}
                  loading={loading}
                >
                  <span>Status</span>
                </Dropdown>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.total ? (
              data.records.map((r, index) => {
                const isExpand = expandIds.includes(r.id);
                const expandClass = isExpand ? styles.expand : '';
                return (
                  <React.Fragment key={r.id}>
                    <tr className={expandClass}>
                      <td>{getNoByIndex(LIMIT_ROWS, filter.page, index)}</td>
                      <td>{r.activity || '-'}</td>
                      <td>{r.model}</td>
                      <td>
                        {r.start_time ? (
                          <>
                            <div>{moment(new Date(r.start_time)).format(DATE_TIME_FORMAT.DATE_SLASH)}</div>
                            <div>{moment(new Date(r.start_time)).format(DATE_TIME_FORMAT.TIME)}</div>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {r.end_time ? (
                          <>
                            <div>{moment(new Date(r.end_time)).format(DATE_TIME_FORMAT.DATE_SLASH)}</div>
                            <div>{moment(new Date(r.end_time)).format(DATE_TIME_FORMAT.TIME)}</div>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <span style={STATUS_STYLE[r.status]}>{r.status}</span>
                        <div>{r.reason}</div>
                      </td>
                      <td>
                        {!isEmpty(r.groups) && (
                          <>
                            <BasicButton mode="icon" onClick={() => handleClickExpand(r.id)}>
                              <img src={dropdown_builder} alt="expand" className={expandClass} style={{ width: '12px' }} />
                            </BasicButton>
                          </>
                        )}
                      </td>
                    </tr>
                    {isExpand &&
                      r?.groups?.map((item) => (
                        <tr key={item.id} style={{ backgroundColor: '#faf7fd' }}>
                          <td></td>
                          <td></td>
                          <td>{item.model}</td>
                          <td>
                            {item.start_time ? (
                              <>
                                <div>{moment(new Date(item.start_time)).format(DATE_TIME_FORMAT.DATE_SLASH)}</div>
                                <div>{moment(new Date(item.start_time)).format(DATE_TIME_FORMAT.TIME)}</div>
                              </>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            {item.end_time ? (
                              <>
                                <div>{moment(new Date(item.end_time)).format(DATE_TIME_FORMAT.DATE_SLASH)}</div>
                                <div>{moment(new Date(item.end_time)).format(DATE_TIME_FORMAT.TIME)}</div>
                              </>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            <span style={STATUS_STYLE[item.status]}>{item.status}</span>
                            <div>{item.reason}</div>
                          </td>
                          <td></td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan="6">
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="mt-3">
          <Pagination {...pagination} />
        </div>
      </div>
    </Spin>
  );
};

export default AiPipelineRun;

const Dropdown = ({ children, options, onApply, loading }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [preSelected, setPreSelected] = useState([]);
  const ref = useRef();

  const handleCheck = (checked, option) => {
    let newSelected = cloneDeep(selected);
    newSelected = checked ? [...newSelected, option] : newSelected.filter((item) => item !== option);
    setSelected(newSelected);
  };

  const handleCheckAll = (checked) => {
    if (checked) {
      setSelected(options);
      return;
    }
    setSelected([]);
  };

  const handleApply = () => {
    onApply(selected);
    setPreSelected(selected);
    setOpen(false);
  };

  useEffect(() => {
    if (options) setSelected(options);
  }, [options]);

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (open && ref.current && !ref.current.contains(e.target) && !loading) {
        setOpen(false);
        setSelected(preSelected);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [open, loading]);

  return (
    <div className={styles.dropdownWrapper} ref={ref}>
      <div className={styles.clickField} onClick={() => setOpen(true)}>
        {children}
        <span className={styles.arrowDown} />
      </div>
      <div className={styles.dropdown} style={open ? { zIndex: 10, opacity: 1 } : { zIndex: -10, opacity: 0 }}>
        <Spin spinning={!options}>
          <div className={styles.list}>
            <div className={styles.showLabel}>Show</div>
            <div className={styles.row}>
              <AiCheckBox onChange={(e) => handleCheckAll(e.target.checked)} checked={selected?.length === options?.length} /> All
            </div>
            {options?.map((option) => (
              <div key={option} className={styles.row}>
                <AiCheckBox checked={selected.includes(option)} onChange={(e) => handleCheck(e.target.checked, option)} /> {option}
              </div>
            ))}
          </div>
        </Spin>
        <div className={styles.btnRow}>
          <BasicButton onClick={() => setSelected([])}>Clear</BasicButton>
          <BasicButton mode="teal" onClick={() => handleApply()}>
            Apply
          </BasicButton>
        </div>
      </div>
    </div>
  );
};

const RangePicker = ({ children, appliedRange, setAppliedRange, loading, type }) => {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState([null, null]);

  const ref = useRef();

  const handleOnChange = (value) => {
    setRange(value);
  };

  const handleApply = () => {
    setAppliedRange(range);
    setAppliedRange({ type, range });
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleResetDate = () => {
    setAppliedRange(INT_APPLIED_RANGE);
    setOpen(false);
  };

  useEffect(() => {
    setRange(appliedRange.range);
  }, [open]);

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (open && ref.current && !ref.current.contains(e.target) && !loading) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [open, loading]);

  return (
    <div className={`${styles.datePicker} ${loading ? styles.disable : ''}`} ref={ref}>
      <div className={styles.clickField} onClick={() => setOpen(true)}>
        {children}
        <span className={styles.arrowDown} />
      </div>
      <div className={styles.dropdown} style={open ? { zIndex: 10, opacity: 1 } : { zIndex: -10, opacity: 0 }}>
        <RangeCalendar value={range} onChange={handleOnChange}>
          <div className={styles.footer}>
            <BasicButton mode="icon" className={styles.resetDate} onClick={handleResetDate}>
              Reset Dates
            </BasicButton>
            <div>
              <BasicButton onClick={handleCancel}>Cancel</BasicButton>
              <BasicButton mode="teal" onClick={handleApply}>
                Apply
              </BasicButton>
            </div>
          </div>
        </RangeCalendar>
      </div>
    </div>
  );
};
